import React from 'react';
import globalHook from 'use-global-hook';
import { MainActionsType } from './models';
import { isEnglish, getPageWidth, getNavbarHeight } from '../utils/helper/helpers';
import { Animation, Animations, CameraConfig, Language } from '../config/models';
import { STYLE } from '../styles/variables';
import { AnimationAction, Camera, LoopOnce, LoopPingPong, LoopRepeat, TextureLoader } from 'three';
import { boardConfig } from '../config/boardConfig';
import { Direction } from '../components/custom/scrollyteller/ScrollyTeller';
import { tweenValueTo } from '../utils/helper/3d/helper';
import { AnimatedLineProps } from '../components/custom/3D/AnimatedLines';
import { cloneDeep, isEqual } from 'lodash';

export interface Trigger extends TriggerConfig {
    index: number;
    id?: string;
    direction?: Direction;
}

export interface TriggerConfig {
    id?: string;
    fade?: string[];
    // TODO: Provide blink!
    camera?: CameraConfig;
    animate?: Animation[];
    transform?: CameraConfig[];
    drawLines?: AnimatedLineProps[];
    reset?: boolean;
    changeMaterial?: { meshId: string; materialName: string };
}

export interface MainStore {
    language: Language;
    loadingProgress: number;
    navbarHeight: string;
    scrollPos: number;
    currentTrigger: Trigger;
    passedTriggers: Trigger[];
    allTriggers: TriggerConfig[];
    cameraInstance?: Camera;
    currentCameraConfig: CameraConfig;
    orbitCameraConfig?: CameraConfig;
    animations: Animations;
    linesToDraw?: AnimatedLineProps[];
    orbitControlsDisabled?: boolean;
    currenAssetTransformation?: CameraConfig;
    objectsToTransform: CameraConfig[];
    passedAnimations?: (AnimationAction & { name: string })[];
    isTransforming?: boolean;
}

export interface MainActions {
    setNavbarHeight: (height: string) => void;
    setScrollTop: (pos: number) => void;
    setLoadingProgress: (loadingProgress: number) => void;
    setCurrentScrollPos: (pos: number) => void;
    setCurrentTrigger: (currentTrigger: Trigger, isUpdate?: boolean) => void;
    setAllTriggers: (allTriggers: TriggerConfig[]) => void;
    setCameraInstance: (camera: Camera) => void;
    setCameraConfig: (config?: CameraConfig) => void;
    setOrbitCameraConfig: (config?: CameraConfig) => void;
    setCurrenAssetTransformation: (currenAssetTransformation?: CameraConfig) => void;
    animateAssets: (animations: Animation[]) => void;
    fadeAssets: (ids: string[], reset?: boolean) => void;
    setAnimations: (animations: Animations) => void;
    changeMaterial: (meshId: string, materialName: string) => void;
    toggleOrbitControls: (enable?: boolean) => void;
    setObjectsToTransform: (objectsToTransform: CameraConfig[]) => void;
    setIsTransforming: (isTransforming?: boolean) => void;
    setDrawingLines: (lines: any[]) => void;
}

const initialState: MainStore = {
    loadingProgress: 0,
    navbarHeight: getNavbarHeight(STYLE.navbar.sizes, getPageWidth()),
    language: isEnglish() ? Language.EN : Language.DE,
    scrollPos: 0,
    currentTrigger: { index: 0 },
    passedTriggers: [],
    allTriggers: [],
    currentCameraConfig: cloneDeep(boardConfig.cameraConfigs.scanner),
    orbitCameraConfig: cloneDeep(boardConfig.cameraConfigs.scanner),
    currenAssetTransformation: undefined,
    animations: {},
    objectsToTransform: []
};

const actions: MainActionsType = {
    setLoadingProgress(store, loadingProgress) {
        store.setState({ loadingProgress });
    },
    setNavbarHeight(store, height) {
        store.setState({ navbarHeight: height });
    },
    setCurrentScrollPos(store, scrollPos) {
        store.setState({ scrollPos });
    },
    setCurrentTrigger(store, currentTrigger) {
        const handleTriggerParams = (trigger: Trigger) => {
            // console.log('FINAL TRIGGER IS', trigger, 'passed triggers after is', store.state.passedTriggers);
            store.setState({ currentTrigger });
            if (trigger.animate) {
                store.actions.animateAssets(trigger.animate);
            }
            if (trigger.fade) {
                store.actions.fadeAssets(trigger.fade, trigger.reset);
            }
            if (trigger.transform) {
                // console.log('SETTING TRANSFORM!');
                store.setState({ objectsToTransform: trigger.transform });
            }
            if (trigger.changeMaterial) {
                store.actions.changeMaterial(trigger.changeMaterial.meshId, trigger.changeMaterial.materialName);
            }
            if (trigger.drawLines) {
                store.setState({ linesToDraw: trigger.drawLines });
            } else {
                store.setState({ linesToDraw: undefined });
            }
            store.actions.setCameraConfig(trigger.camera);
        };

        // console.log('Passed triggers are', store.state.passedTriggers);
        let adaptedTrigger = cloneDeep(currentTrigger);
        if (currentTrigger.direction === Direction.UP) {
            const passedTriggers = [...store.state.passedTriggers];
            // console.log('Passed triggers are', passedTriggers);
            const lastTrigger: Trigger = (passedTriggers as any).at(-1);
            adaptedTrigger.fade = [...(lastTrigger?.fade || [])];
            adaptedTrigger.animate = lastTrigger?.animate?.map(a => ({
                ...a,
                action: a.action === 'reverse' ? 'start' : 'reverse'
            }));
            if (!adaptedTrigger.camera) {
                adaptedTrigger.camera = ([...passedTriggers].slice(0, -1) as any).findLast(
                    (t: Trigger) => t.camera
                )?.camera;
            }
            if (!adaptedTrigger.transform) {
                adaptedTrigger.transform = ([...passedTriggers].slice(0, -1) as any).findLast(
                    (t: Trigger) => t.transform && t.transform.length > 0
                )?.transform;
            }
            store.setState({ passedTriggers: [...store.state.passedTriggers].slice(0, -1) });
            handleTriggerParams(adaptedTrigger);
        } else if (!store.state.passedTriggers.find(p => isEqual(p, currentTrigger))) {
            // console.log('NOT EQUAL!');
            store.setState({ passedTriggers: [...store.state.passedTriggers, currentTrigger] });
            handleTriggerParams(currentTrigger);
        }
    },
    setAllTriggers(store, allTriggers) {
        store.setState({ allTriggers });
    },
    setScrollTop(store, scrollPos) {
        store.setState({ scrollPos });
        if (scrollPos === 0) {
            window.scrollTo(0, 0);
            store.setState({ passedTriggers: [] });
            store.actions.setCurrentTrigger({
                index: 0,
                reset: true,
                fade: boardConfig.opacityAdjustmentList,
                camera: boardConfig.cameraConfigs['scanner'],
                transform: [boardConfig.assetTransformations['scanner']['initial']],
                changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_home.jpg' }
            });
        }
    },
    setCameraInstance(store, cameraInstance) {
        store.setState({ cameraInstance });
    },
    setCameraConfig(store, cameraConfig) {
        const currentCameraConfig = store.state.currentCameraConfig;

        store.setState({
            currentCameraConfig: {
                ...currentCameraConfig,
                position: cameraConfig?.position.clone() || currentCameraConfig.position,
                quaternion: cameraConfig?.quaternion.clone() || currentCameraConfig.quaternion,
                rotation: cameraConfig?.rotation?.clone() || currentCameraConfig.rotation,
                target: cameraConfig?.target?.clone() || currentCameraConfig.target
            }
        });
        store.actions.toggleOrbitControls(false);
    },
    setOrbitCameraConfig(store, orbitCameraConfig) {
        store.setState({
            orbitCameraConfig
        });
    },
    animateAssets(store, animations) {
        animations.map(({ name, mode = 'LoopOnce', repetitions = 0, action = 'start' }) => {
            try {
                const animationMode = (() => {
                    switch (mode) {
                        case 'LoopOnce':
                            return LoopOnce;
                        case 'Loop':
                            return LoopRepeat;
                        case 'LoopPingPong':
                            return LoopPingPong;
                        default:
                            return LoopOnce;
                    }
                })();
                const animationObject = store.state.animations[name] || store.state.animations[name + 'Action'];
                animationObject.clampWhenFinished = mode === 'LoopOnce';
                animationObject.name = name;
                const clip = animationObject.getClip();
                const multipleAnimations = store.state.passedAnimations?.filter(pa =>
                    pa.getClip().tracks.some(r => clip.tracks.find(t => t.name === r.name))
                );
                if (action !== 'reverse') {
                    multipleAnimations?.forEach(a => a.stop());
                }
                if (action === 'stop') {
                    animationObject.stop();
                } else if (action === 'start') {
                    animationObject.timeScale = 1;
                    animationObject.setLoop(animationMode, repetitions).play();
                } else if (action === 'reverse') {
                    animationObject.paused = false;
                    animationObject.timeScale = -1;
                    animationObject.setLoop(animationMode, repetitions).play();
                }

                if (!store.state.passedAnimations?.find(a => a.name === name)) {
                    store.setState({ passedAnimations: [...(store.state.passedAnimations || []), animationObject] });
                }
            } catch (e) {
                console.error(
                    'Failed animation for:',
                    name,
                    'Error:',
                    e,
                    '\nPlease choose animations from:',
                    Object.keys(store.state.animations)
                );
            }
        });
    },
    fadeAssets(store, ids, reset) {
        const meshes = boardConfig.assets.filter(
            h => ids.includes(h.id) || (h.ref?.parent?.name && ids.includes(h.ref?.parent?.name))
        );
        // console.log('FOUND MESHES TO FADE', meshes);
        if (meshes && meshes.length > 0) {
            meshes.forEach(mesh => {
                if (mesh.ref) {
                    // console.log('FADING', mesh.id, mesh.fading === 'in' ? 'out' : 'in');
                    tweenValueTo(
                        mesh.ref.material,
                        { opacity: mesh.fading === 'in' || reset ? 0 : boardConfig.maxOpacity },
                        1500
                    );

                    setTimeout(() => {
                        mesh.ref!.material.depthWrite = mesh.ref!.material.opacity !== boardConfig.maxOpacity;
                    }, 1000);
                }

                if (mesh.ref?.children) {
                    mesh.ref?.children.forEach(childMesh => {
                        if ((childMesh as any).material) {
                            tweenValueTo(
                                (childMesh as any).material,
                                {
                                    opacity: mesh.fading === 'in' || reset ? 0 : boardConfig.maxOpacity
                                },
                                1500
                            );
                        }
                    });
                }
                mesh.fading = mesh.fading === 'in' || reset ? 'out' : 'in';
            });
        } else {
            console.error(`Could not find mesh for fadeIn with boardId: ${ids}`);
        }
    },

    setAnimations(store, animations) {
        store.setState({ animations });
    },
    setIsTransforming(store, isTransforming) {
        store.setState({ isTransforming });
    },
    setObjectsToTransform(store, objectsToTransform) {
        store.setState({ objectsToTransform });
    },
    setCurrenAssetTransformation(store, currenAssetTransformation) {
        store.setState({ currenAssetTransformation });
    },
    setDrawingLines(store, linesToDraw) {
        store.setState({ linesToDraw });
    },
    toggleOrbitControls(store, enabled) {
        store.setState({ orbitControlsDisabled: !enabled });
    },
    changeMaterial(store, meshName: string, newMaterialMap: string) {
        const mesh = boardConfig.assets.find(h => meshName === h.id);
        if (mesh && mesh.ref) {
            try {
                const flipY = false;
                const encoding = mesh.ref!.material.map!.encoding;
                mesh.ref.material.map = new TextureLoader().load(
                    `${isEnglish() ? '../' : ''}media/textures/${newMaterialMap}`,
                    texture => {
                        mesh.ref!.material.map!.flipY = flipY;
                        mesh.ref!.material.map!.encoding = encoding;
                        mesh.ref!.material.map!.name =
                            newMaterialMap
                                ?.split(/[\\/]/)
                                ?.pop()
                                ?.replace(/\.[^/.]+$/, '') || '';
                        mesh.ref!.material.map!.needsUpdate = true;
                        texture.needsUpdate = true;
                    }
                );
            } catch (e) {
                console.error('Could not update texture', e);
            }
        } else {
            console.error(`No mesh found for ${meshName}`, boardConfig.assets);
        }
    }
};

export const useGlobal = globalHook<MainStore, MainActions>(React, initialState, actions);
